/**
 * The document as one whole-body read shows it, paragraph by paragraph, in the
 * records the agent's own view of a document is rendered from.
 *
 * WHY the reader's records
 *   The agent reads a document as the core renders the web reader's
 *   `DocumentParagraph`s: accepted text, pending tracked changes, comments,
 *   numbering. A view built from the same records, by the same reader
 *   (`OoxmlRange.documentParagraphs`), shows a changed paragraph to the agent
 *   in exactly the form it already knows, and the core renders it with the same
 *   code, so the two can never drift apart.
 *
 * WHAT a view answers
 *   - which paragraph a Word event's `uniqueLocalId` names (`paragraphWithUniqueLocalId`)
 *   - what changed since an earlier view, to the paragraph (`changesSince`)
 */
import type { DocumentChange, ParagraphReference, RenumberedParagraph } from "@common/types/document-changes-for-agent";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { DocumentComments } from "@common/utils/redline/ooxml/document-comments";
import { OoxmlNumbering } from "@common/utils/redline/ooxml/ooxml-numbering";
import { OoxmlOutlineLevels } from "@common/utils/redline/ooxml/ooxml-outline-levels";
import { wordDiff } from "@common/utils/redline/text/word-diff";
import type { DocumentParagraph } from "@common/utils/redline/wire/document-paragraph";

import type { BodyRead } from "./word-document";

export class DocumentView {
    private constructor(
        /** Every body paragraph in document order, as the reader reports it. */
        readonly paragraphs: DocumentParagraph[],
        /** The paragraph each `uniqueLocalId` of the read names; empty when the read's ids did not line up with its paragraphs. */
        private readonly paragraphByUniqueLocalId: ReadonlyMap<string, DocumentParagraph>,
        /** The document event stream's last sequence when the read began: every change published at or before it is in this view. */
        readonly coversEventsThroughSequence: number,
    ) {}

    /**
     * The view one whole-body read shows: the package walked by the reader with
     * the comments, numbering and heading levels its own parts carry.
     *
     * WHY by index
     *   `Body.paragraphs` and the reader list the same paragraphs in document
     *   order (body and table cell paragraphs, none nested in another), so the
     *   read's `uniqueLocalId`s name the reader's paragraphs by position, once the
     *   empty paragraph Word exports past the body's end is left out
     *   (`OoxmlRange.paragraphCountLiningUpWithWord`); the view leaves it out too.
     *
     * EDGE CASE
     *   The counts differ some other way: no id can be trusted to its paragraph,
     *   so the view names none, and a Word event about any paragraph is reported
     *   as one the view cannot identify.
     */
    static fromBodyRead(bodyRead: BodyRead): DocumentView {
        const bodyPackage = new OoxmlRange(bodyRead.flatOpc);
        const packageParagraphs = bodyPackage.documentParagraphs(
            DocumentComments.fromPackage(bodyRead.flatOpc),
            OoxmlNumbering.fromPackage(bodyRead.flatOpc),
            OoxmlOutlineLevels.fromPackage(bodyRead.flatOpc),
        );
        const bodyParagraphCount = bodyPackage.paragraphCountLiningUpWithWord(bodyRead.paragraphUniqueLocalIds.length);
        /**
         * CASES
         *   - the package lines up with Word's list -> its first paragraphs, as many as Word lists
         *   - it does not                           -> null: no id is trusted to a paragraph
         *
         * EXAMPLE
         *   Word 226, package 227 -> slice(0, 226): Word's closing paragraph left out
         */
        const bodyParagraphs = bodyParagraphCount === null ? null : packageParagraphs.slice(0, bodyParagraphCount);
        const paragraphByUniqueLocalId = new Map<string, DocumentParagraph>(
            bodyParagraphs === null ? [] : bodyRead.paragraphUniqueLocalIds.map((uniqueLocalId, paragraphIndex) => [uniqueLocalId, bodyParagraphs[paragraphIndex]]),
        );
        return new DocumentView(bodyParagraphs ?? packageParagraphs, paragraphByUniqueLocalId, bodyRead.coversEventsThroughSequence);
    }

    /**
     * The paragraph a Word event names, as this view read it.
     *
     * NULL
     *   The id names no paragraph this view read: one added after the read, or a
     *   read whose ids did not line up with its paragraphs.
     */
    paragraphWithUniqueLocalId(uniqueLocalId: string): DocumentParagraph | null {
        return this.paragraphByUniqueLocalId.get(uniqueLocalId) ?? null;
    }

    /**
     * What changed from `earlier` to this view, to the paragraph, in document order.
     *
     * HOW
     *   The two paragraph lists are aligned by `wordDiff`, the engine's Myers
     *   diff, over each paragraph's identity: the paragraph bookmark that names it,
     *   which survives every edit of its text. A paragraph no naming pass has
     *   reached yet goes by its accepted text instead.
     *   - a paragraph both views hold      -> compared (`comparedChange`)
     *   - a run of paragraphs replaced by
     *     another run                      -> paired in order and compared; the
     *                                         longer run's extra paragraphs are
     *                                         added or deleted
     *   - a paragraph only this view holds -> added, after the paragraph before it
     *   - a paragraph only `earlier` holds -> deleted, from after the paragraph before it
     *
     * WHY label-only changes are gathered
     *   A paragraph inserted into a numbered list renumbers every item after it;
     *   showing each as a changed paragraph would bury the edit. They come back as
     *   one `paragraphs_renumbered` change, after the others.
     *
     * EXAMPLE
     *   earlier  _a "Fees are due."    _b "Term is one year."   _c "(3) Notice."
     *   this     _a "Fees are due."    _n "New clause."         _b "Term is two years."   _c "(4) Notice."
     *   -> paragraph_added _n after _a, paragraph_changed _b, paragraphs_renumbered _c "(3)" -> "(4)"
     *      (with "(3)" and "(4)" as numbering labels, not typed text)
     */
    changesSince(earlier: DocumentView): DocumentChange[] {
        return this.changesAlignedBy(earlier, (paragraph) => paragraph.paragraphBookmarkName ?? `unnamed:${paragraph.allChangesAcceptedText}`);
    }

    /**
     * What changed from `earlier` to this view, with the two paragraph lists aligned
     * by Word's own id for each paragraph (`uniqueLocalId`) instead of its paragraph bookmark.
     *
     * WHY
     *   When the paragraph bookmarks were lost or replaced (`paragraphIdsChangedSince`),
     *   the paragraphs Word still holds carry none of the identities they had in
     *   `earlier`, and an alignment by bookmark pairs none of them: every one would read
     *   as replaced. Word's id stays on a paragraph through the loss of its bookmark, so
     *   each is matched with itself and only what really changed is reported, under the
     *   paragraph's new id.
     *
     * EXAMPLE
     *   earlier  p-1 _a "Fees are due."    p-2 _b "Term is one year."
     *   this     p-1 _x "Fees are due."    p-2 _y "Term is two years."
     *   -> paragraph_changed _b -> _y; _a -> _x is no change
     *
     * EDGE CASE
     *   A read whose ids did not line up with its paragraphs names none of them; its
     *   paragraphs go by their accepted text. `paragraphIdsChangedSince` counts no
     *   paragraph in such a read, so a message under new ids never aligns one.
     */
    changesByWordParagraphIdSince(earlier: DocumentView): DocumentChange[] {
        const uniqueLocalIdByParagraph = new Map([...earlier.uniqueLocalIdByParagraph(), ...this.uniqueLocalIdByParagraph()]);
        return this.changesAlignedBy(earlier, (paragraph) => uniqueLocalIdByParagraph.get(paragraph) ?? `unidentified:${paragraph.allChangesAcceptedText}`);
    }

    /**
     * How many paragraphs `earlier` named by a paragraph bookmark that Word still holds
     * in this view under another paragraph bookmark, or under none: the paragraph is
     * still there, its id is not.
     *
     * WHY
     *   The agent refers to paragraphs by their ids. A paragraph Word kept while its
     *   bookmark changed is, aligned by bookmark, one paragraph deleted and another
     *   added, yet it is the same paragraph, and every id the agent holds for it is
     *   stale (bookmarks a tool stripped, names minted afresh after a reload).
     *
     * WHY by Word's id
     *   Word's `uniqueLocalId` stays on a paragraph for the session whatever happens
     *   to its bookmarks, and leaves with it when the paragraph is deleted. Only it tells
     *   a renamed paragraph from one deleted beside an added one that reads alike, such as
     *   a blank line deleted and another typed: every blank line reads alike.
     *
     * EXAMPLE
     *   earlier  p-1 _a "Fees are due."   p-2 _b "Term is one year."    p-3 _c ""
     *   this     p-1 _x "Fees are due."   p-2 _b "Term is two years."   p-9 _d ""
     *   -> 1: p-1 now carries _x; p-2 kept _b; p-3 was deleted and p-9 added
     *
     * EDGE CASE
     *   A read whose ids did not line up with its paragraphs names no paragraph by
     *   Word's id: no paragraph can be followed across the two reads, and none is counted.
     */
    paragraphIdsChangedSince(earlier: DocumentView): number {
        let paragraphsWithChangedIds = 0;
        for (const [uniqueLocalId, earlierParagraph] of earlier.paragraphByUniqueLocalId) {
            const laterParagraph = this.paragraphByUniqueLocalId.get(uniqueLocalId);
            /**
             * CASES, not counted
             *   - the earlier paragraph had no bookmark -> the agent held no id for it
             *   - Word no longer holds the paragraph    -> deleted, not renamed
             *   - it carries the same bookmark          -> the agent's id for it still holds
             */
            if (earlierParagraph.paragraphBookmarkName === null || laterParagraph === undefined || laterParagraph.paragraphBookmarkName === earlierParagraph.paragraphBookmarkName) continue;
            paragraphsWithChangedIds += 1;
        }
        return paragraphsWithChangedIds;
    }

    /** Word's id for each paragraph this view read, the other way round from `paragraphByUniqueLocalId`; empty when the read's ids did not line up with its paragraphs. */
    private uniqueLocalIdByParagraph(): Map<DocumentParagraph, string> {
        return new Map(Array.from(this.paragraphByUniqueLocalId, ([uniqueLocalId, paragraph]) => [paragraph, uniqueLocalId]));
    }

    /**
     * What changed from `earlier` to this view, the two paragraph lists aligned by
     * `identityOf`; `changesSince` and `changesByWordParagraphIdSince` say which identity.
     */
    private changesAlignedBy(earlier: DocumentView, identityOf: (paragraph: DocumentParagraph) => string): DocumentChange[] {
        const changes: DocumentChange[] = [];
        const renumbered: RenumberedParagraph[] = [];
        /**
         * Whether the paragraph bookmark the agent holds for `before` names no paragraph
         * once `after`, another paragraph, took its place in a replaced run.
         *
         * WHY
         *   Paragraphs paired across a replaced run can read alike: a blank line Word
         *   replaced with another, or a body pasted over itself whose blank lines and
         *   repeated clauses come back under fresh bookmarks. Compared by what they read
         *   alone they are no change, and the agent keeps an id that names no paragraph.
         *
         * CASES
         *   - `after` is `before` itself (one identity) -> no: the alignment followed it, and
         *                                                  a message under new ids hands the
         *                                                  agent every new id or asks it to
         *                                                  read again
         *   - `before` had no bookmark                  -> no: the agent named it by its place
         *   - `after` carries `before`'s bookmark       -> no: the naming pass laid it back
         *   - otherwise                                 -> yes
         *
         * EXAMPLE
         *   earlier  p-2 _blank_line_one ""
         *   this     p-9 _blank_line_new ""
         *   -> yes: paragraph_changed, before _blank_line_one, after _blank_line_new
         */
        const agentBookmarkLostToReplacement = (before: DocumentParagraph, after: DocumentParagraph): boolean =>
            identityOf(before) !== identityOf(after) && before.paragraphBookmarkName !== null && before.paragraphBookmarkName !== after.paragraphBookmarkName;
        /**
         * The change one paragraph both views hold made, if any.
         *
         * CASES
         *   - its text, tracked changes, comments, or how deep
         *     in table cells it sits changed                    -> paragraph_changed, both versions
         *   - another paragraph took its place and the bookmark
         *     the agent holds for it went (`agentBookmarkLostToReplacement`)
         *                                                       -> paragraph_changed, both versions,
         *                                                          so the agent reads both ids
         *   - only the label Word paints before it changed      -> renumbered
         *   - nothing the agent reads changed                   -> no change
         */
        const comparedChange = (before: DocumentParagraph, after: DocumentParagraph): void => {
            if (DocumentView.readingOf(before) !== DocumentView.readingOf(after) || agentBookmarkLostToReplacement(before, after)) {
                changes.push({ kind: "paragraph_changed", before, after });
                return;
            }
            if (before.numberingLabel !== after.numberingLabel) renumbered.push({ paragraph: DocumentView.referenceTo(after), labelBefore: before.numberingLabel, labelAfter: after.numberingLabel });
        };
        /**
         * The paragraph just before position `paragraphIndex` of `paragraphs`.
         *
         * NULL
         *   The position is the first: the paragraph opens the document.
         *
         * EXAMPLE
         *   paragraphIndex = 3 -> `3 - 1` = 2, the paragraph before it
         *   paragraphIndex = 0 -> `0 - 1` = -1, no paragraph -> null
         */
        const paragraphBefore = (paragraphs: DocumentParagraph[], paragraphIndex: number): ParagraphReference | null => {
            const previousParagraph = paragraphs[paragraphIndex - 1];
            return previousParagraph === undefined ? null : DocumentView.referenceTo(previousParagraph);
        };
        for (const hunk of wordDiff(earlier.paragraphs.map(identityOf), this.paragraphs.map(identityOf))) {
            /**
             * EXAMPLE
             *   hunk before 4..6, after 4..5 -> beforeCount 2, afterCount 1, pairedCount 1:
             *   earlier 4 is compared with this 4, earlier 5 is deleted
             */
            const beforeCount = hunk.beforeEnd - hunk.beforeStart;
            const afterCount = hunk.afterEnd - hunk.afterStart;
            const pairedCount = Math.min(beforeCount, afterCount);
            for (let offset = 0; offset < pairedCount; offset++) comparedChange(earlier.paragraphs[hunk.beforeStart + offset], this.paragraphs[hunk.afterStart + offset]);
            for (let offset = pairedCount; offset < beforeCount; offset++) {
                const deletedIndex = hunk.beforeStart + offset;
                changes.push({ kind: "paragraph_deleted", before: earlier.paragraphs[deletedIndex], followedParagraph: paragraphBefore(earlier.paragraphs, deletedIndex) });
            }
            for (let offset = pairedCount; offset < afterCount; offset++) {
                const addedIndex = hunk.afterStart + offset;
                changes.push({ kind: "paragraph_added", after: this.paragraphs[addedIndex], followsParagraph: paragraphBefore(this.paragraphs, addedIndex) });
            }
        }
        /**
         * WHEN no paragraph changed only its label
         *   CONSEQUENCE
         *     no renumbering change: the change list holds no empty entry.
         */
        if (renumbered.length > 0) changes.push({ kind: "paragraphs_renumbered", paragraphs: renumbered });
        return changes;
    }

    /**
     * The fewest paragraphs `changesSince(earlier)` can report as changed, added or
     * deleted, counted without aligning the two views.
     *
     * HOW
     *   A paragraph reports no change only when it is compared with a paragraph of
     *   the other view that reads the same (`readingOf`), one to one. So no more
     *   paragraphs go unchanged than the two views share readings, repeats counted;
     *   every other paragraph of the longer view is changed, added or deleted.
     *
     * WHY
     *   The alignment (`wordDiff`) costs time and memory in step with how many
     *   paragraphs differ: a body pasted over costs it close to half a second and
     *   hundreds of megabytes. This count is one pass over each view, and tells
     *   before any alignment that the changes cannot fit a budget.
     *
     * EXAMPLE
     *   earlier reads   A B C D
     *   this reads      A X C Y Z
     *   shared readings A, C                 -> 2
     *   longer view     5 paragraphs         -> at least 5 - 2 = 3 changes
     *   changesSince    B -> X, D -> Y, Z added  (3)
     */
    fewestParagraphChangesSince(earlier: DocumentView): number {
        const unmatchedEarlierCountByReading = new Map<string, number>();
        for (const earlierParagraph of earlier.paragraphs) {
            const earlierReading = DocumentView.readingOf(earlierParagraph);
            unmatchedEarlierCountByReading.set(earlierReading, (unmatchedEarlierCountByReading.get(earlierReading) ?? 0) + 1);
        }
        let sharedReadingCount = 0;
        for (const paragraph of this.paragraphs) {
            const reading = DocumentView.readingOf(paragraph);
            const unmatchedEarlierCount = unmatchedEarlierCountByReading.get(reading) ?? 0;
            /**
             * WHEN no earlier paragraph that reads the same is left to match
             *   CONSEQUENCE
             *     this paragraph cannot go unchanged; it is not counted as shared.
             */
            if (unmatchedEarlierCount === 0) continue;
            /**
             * EXAMPLE
             *   two earlier paragraphs read "Reserved.", this is the first of ours to
             *   match one -> 2 - 1 = 1 left for the next "Reserved." of ours
             */
            unmatchedEarlierCountByReading.set(reading, unmatchedEarlierCount - 1);
            sharedReadingCount += 1;
        }
        return Math.max(earlier.paragraphs.length, this.paragraphs.length) - sharedReadingCount;
    }

    /**
     * How many characters of accepted text the whole view holds: how long the document
     * is as the agent reads it, without the markup around each paragraph.
     *
     * EXAMPLE
     *   "Fees are due." (13) and "The term is one year." (21) -> 34
     */
    acceptedTextCharacterCount(): number {
        return this.paragraphs.reduce((characterCount, paragraph) => characterCount + paragraph.allChangesAcceptedText.length, 0);
    }

    /**
     * The paragraph a card names by one of its bookmarks, as this view read it.
     *
     * WHY every bookmark, not only the paragraph's id
     *   A card names its paragraphs by the bookmarks it was proposed on; the reader picks
     *   one bookmark per paragraph as its id (`paragraphBookmarkName`) and lists the rest
     *   (`bookmarkNames`), so a card's bookmark is found among either.
     *
     * NULL
     *   No paragraph this view read carries the bookmark: one the card's write removed,
     *   one added after the read, or a bookmark Word dropped.
     */
    paragraphWithBookmarkName(bookmarkName: string): DocumentParagraph | null {
        return this.paragraphs.find((paragraph) => paragraph.paragraphBookmarkName === bookmarkName || paragraph.bookmarkNames.includes(bookmarkName)) ?? null;
    }

    /**
     * True when the agent would read the two paragraphs the same (`readingOf`): the
     * same accepted text, pending changes, comments and cell depth.
     */
    static readTheSame(earlier: DocumentParagraph, later: DocumentParagraph): boolean {
        return DocumentView.readingOf(earlier) === DocumentView.readingOf(later);
    }

    /** How the agent can name a paragraph: its paragraph bookmark, or its position when it has none. */
    static referenceTo(paragraph: DocumentParagraph): ParagraphReference {
        return { paragraphBookmarkName: paragraph.paragraphBookmarkName, paragraphIndex: paragraph.paragraphIndex };
    }

    /**
     * Everything about a paragraph the agent reads, as one comparable string: its
     * accepted text, its pending changes as the agent sees them (which carries the
     * pictures and the struck and underlined runs), its comments, and how many
     * table cells deep it sits.
     *
     * WHY no position
     *   The paragraph's index, its numbering label and its cell's numbers (the
     *   table's number in the document, the row's number, the row and cell counts)
     *   all move when something else is added around it: a row added to a table
     *   would otherwise report every cell of that table as changed. The label is
     *   read apart (`changesSince`); a cell whose words change is caught by its text.
     *
     * EXAMPLE
     *   a 5-row fee table gains the row "Audit | $1,500"
     *   -> its two cells are added; the other ten cells, whose row count went 5 -> 6, read the same
     */
    private static readingOf(paragraph: DocumentParagraph): string {
        return JSON.stringify([paragraph.allChangesAcceptedText, paragraph.agentViewText, paragraph.comments, paragraph.tableCellPath.length]);
    }
}
